import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  RadialBarChart, RadialBar, ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip
} from 'recharts';
import { clearAuth } from '../utils/authUtil';
import { usePopup } from '../context/PopupContext';
import './StudentPortal.css';

const StudentPortal = () => {
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('30');

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await API.get('/portal/dashboard');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm('Logout', 'Are you sure you want to logout?');
    if (confirmed) {
      clearAuth();
      navigate('/');
    }
  };

  if (loading) return (
    <div className="portal-loading">
      <div className="portal-spinner" />
      <p>Loading your dashboard...</p>
    </div>
  );

  if (!data) return (
    <div className="portal-loading">
      <p>Failed to load dashboard. Please login again.</p>
      <button onClick={handleLogout}>Go to Login</button>
    </div>
  );

  const { student, assignedTests, recentAttempts, topicStats, overallStats, readiness, rank } = data;

  const pendingTests  = assignedTests.filter(t => t.attempt_status === 'in_progress' || t.attempt_status === 'not_started');
  // eslint-disable-next-line
  const completedTests = assignedTests.filter(t => t.attempt_status === 'submitted');

  const getReadinessColor = (level) => {
    const map = {
      'top_performer':    '#16a085',
      'placement_ready':  '#2980b9',
      'moderate':         '#f39c12',
      'needs_improvement':'#e94560',
    };
    return map[level] || '#888';
  };

  const getReadinessLabel = (level) => {
    const map = {
      'top_performer':    'Top Performer',
      'placement_ready':  'Placement Ready',
      'moderate':         'Moderate',
      'needs_improvement':'Needs Improvement',
    };
    return map[level] || 'Not evaluated';
  };

  const pieData = [
    { name: 'Correct', value: parseInt(overallStats?.total_correct) || 0 },
    { name: 'Wrong',   value: parseInt(overallStats?.total_wrong)   || 0 },
  ];

  const accuracy = overallStats?.total_attempted > 0
    ? ((overallStats.total_correct / overallStats.total_attempted) * 100).toFixed(1)
    : 0;

  const getScoreColor = (score, total) => {
    const pct = (score / total) * 100;
    if (pct >= 80) return '#16a085';
    if (pct >= 60) return '#f39c12';
    return '#e94560';
  };

  const getScoreLabel = (score, total) => {
    const pct = (score / total) * 100;
    if (pct >= 80) return { label: 'Excellent', bg: '#e6f9f0', color: '#1a7f3c' };
    if (pct >= 60) return { label: 'Good',      bg: '#fff8e6', color: '#b37400' };
    return                { label: 'Average',   bg: '#fff0f0', color: '#cc0000' };
  };

  const getMasteryLevel = (pct) => {
    if (pct >= 80) return { label: 'Expert',    color: '#16a085' };
    if (pct >= 60) return { label: 'Mid Level', color: '#f39c12' };
    return                { label: 'Beginner',  color: '#e94560' };
  };

  return (
    <>
      <div className="portal-body">

        {/* Welcome + Quick start */}
        <div className="portal-welcome-row">
          <div>
            <h2 className="portal-welcome">Welcome back, {student.name.split(' ')[0]}! 👋</h2>
            <p className="portal-welcome-sub">Ready to ace your next aptitude challenge?</p>
            <div className="student-profile-chips">
              {student.branch && <span className="profile-chip">🎓 {student.branch}</span>}
              {student.cgpa > 0 && <span className="profile-chip">📊 CGPA: {parseFloat(student.cgpa).toFixed(2)}</span>}
              {student.arrears > 0 && <span className="profile-chip arrears-chip">⚠️ Arrears: {student.arrears}</span>}
              {student.placement_batch_year && <span className="profile-chip">📅 Batch {student.placement_batch_year}</span>}
              {student.year_of_study && <span className="profile-chip">📚 Year {student.year_of_study}</span>}
            </div>
          </div>
          <button className="btn-quick-start"
            onClick={() => pendingTests.length > 0 && navigate(`/student/test/${pendingTests[0].id}`)}>
            ⚡ Quick Start Practice
          </button>
        </div>

        <div className="portal-grid">
          <div className="portal-left">

            {/* Current Progress */}
            <div className="portal-card">
              <div className="portal-card-header">
                <span className="portal-card-title">Current Progress</span>
                <select className="portal-select" value={timeFilter}
                  onChange={e => setTimeFilter(e.target.value)}>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                </select>
              </div>
              {topicStats.length === 0 ? (
                <div className="portal-empty">No test data yet. Take a test to see progress!</div>
              ) : (
                <div className="progress-circles">
                  {topicStats.slice(0, 3).map((t, i) => {
                    const pct = parseFloat(t.score_percentage) || 0;
                    const mastery = getMasteryLevel(pct);
                    const colors = ['#0f3460', '#8e44ad', '#16a085'];
                    return (
                      <div key={i} className="progress-circle-item">
                        <div className="circle-chart">
                          <ResponsiveContainer width={90} height={90}>
                            <RadialBarChart cx="50%" cy="50%"
                              innerRadius="60%" outerRadius="90%"
                              data={[{ value: pct, fill: colors[i % colors.length] }]}
                              startAngle={90} endAngle={-270}>
                              <RadialBar dataKey="value" cornerRadius={4} background={{ fill: '#f0f0f0' }} />
                            </RadialBarChart>
                          </ResponsiveContainer>
                          <div className="circle-pct">{pct.toFixed(0)}%</div>
                        </div>
                        <div className="circle-label">{t.topic}</div>
                        <div className="circle-mastery" style={{ color: mastery.color }}>
                          Mastery: {mastery.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="portal-card">
              <div className="portal-card-header">
                <span className="portal-card-title">Recent Activity</span>
              </div>
              {recentAttempts.length === 0 ? (
                <div className="portal-empty">No tests taken yet.</div>
              ) : (
                <div className="activity-table-wrapper">
                  <table className="activity-table">
                    <thead>
                      <tr>
                        <th>Test Name</th>
                        <th>Date</th>
                        <th>Score</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentAttempts.map((a, i) => {
    const scoreLabel = getScoreLabel(a.score, a.total_marks);
    return (
      <tr key={i}>
        <td className="act-name">{a.test_name}</td>
        <td className="act-date">
          {new Date(a.attempted_at).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric'
          })}
        </td>
        <td>
          <span className="act-score"
            style={{ color: getScoreColor(a.score, a.total_marks) }}>
            {a.score}/{a.total_marks}
          </span>
        </td>
        <td>
          <span className="act-badge"
            style={{ background: scoreLabel.bg, color: scoreLabel.color }}>
            {scoreLabel.label}
          </span>
        </td>
        <td>
          <button className="act-details"
            onClick={() => navigate('/student/tests')}>
            Details
          </button>
        </td>
      </tr>
    );
  })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="portal-right">

            {/* Upcoming Tests */}
            <div className="portal-card">
              <div className="portal-card-header">
                <span className="portal-card-title">Upcoming Tests</span>
              </div>
              {pendingTests.length === 0 ? (
                <div className="portal-empty">No pending tests 🎉</div>
              ) : (
                <div className="upcoming-list">
                  {pendingTests.slice(0, 4).map((t, i) => (
                    <div key={i} className="upcoming-item">
                      <div className="upcoming-info">
                        <div className="upcoming-name">{t.test_name}</div>
                        <div className="upcoming-meta">
                          {t.scheduled_at
                            ? new Date(t.scheduled_at).toLocaleDateString('en-IN')
                            : 'Available now'
                          } · {t.duration_minutes} mins
                        </div>
                      </div>
                      <button
                        className={`upcoming-btn ${i === 0 ? 'start' : 'wait'}`}
                        onClick={() => i === 0 && navigate(`/student/test/${t.id}`)}>
                        {i === 0 ? 'Start' : 'View'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Overall Performance */}
            <div className="portal-card perf-card">
              <div className="portal-card-title" style={{ marginBottom: 16 }}>Overall Performance</div>
              <div className="perf-stats">
                <div className="perf-stat">
                  <div className="perf-stat-val">{parseFloat(overallStats?.avg_score || 0).toFixed(1)}%</div>
                  <div className="perf-stat-label">Avg Score</div>
                </div>
                <div className="perf-stat">
                  <div className="perf-stat-val">{overallStats?.tests_taken || 0}</div>
                  <div className="perf-stat-label">Tests Taken</div>
                </div>
                <div className="perf-stat">
                  <div className="perf-stat-val">#{rank?.rank || '—'}</div>
                  <div className="perf-stat-label">Rank</div>
                </div>
              </div>

              {parseInt(overallStats?.total_attempted) > 0 && (
                <div className="accuracy-row">
                  <ResponsiveContainer width={80} height={80}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%"
                        innerRadius={25} outerRadius={38} dataKey="value" paddingAngle={2}>
                        <Cell fill="#16a085" />
                        <Cell fill="#e94560" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div>
                    <div className="accuracy-val">{accuracy}%</div>
                    <div className="accuracy-label">Overall Accuracy</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                      {overallStats?.total_correct} correct / {overallStats?.total_wrong} wrong
                    </div>
                  </div>
                </div>
              )}

              {readiness && (
                <div className="readiness-box"
                  style={{ borderColor: getReadinessColor(readiness.readiness_level) }}>
                  <div className="readiness-icon">🎯</div>
                  <div>
                    <div className="readiness-title"
                      style={{ color: getReadinessColor(readiness.readiness_level) }}>
                      {getReadinessLabel(readiness.readiness_level)}
                    </div>
                    <div className="readiness-sub">
                      Score: {parseFloat(readiness.overall_score).toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}
            </div>

            {topicStats.length > 0 && (
              <div className="portal-card">
                <div className="portal-card-title" style={{ marginBottom: 14 }}>Topic Breakdown</div>
                {topicStats.map((t, i) => {
                  const pct = parseFloat(t.score_percentage) || 0;
                  const color = pct >= 80 ? '#16a085' : pct >= 60 ? '#f39c12' : '#e94560';
                  return (
                    <div key={i} className="topic-breakdown-row">
                      <div className="tb-name">{t.topic}</div>
                      <div className="tb-bar-bg">
                        <div className="tb-bar-fill" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <div className="tb-pct" style={{ color }}>{pct.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentPortal;